import APP_ASSETS from "@/assets";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { LocalStorageKeys } from "@/constants";
import { useMerchant, useSmartWallet, useUSDT } from "@/contexts";
import { getMerchantUpi } from "@/helper/localstorage";
import { copyToClipboard, maskAddress } from "@/lib/utils";
import { useEffect, useState } from "react";

const StakeDetails = () => {
  const [encUpiId, setEncUpiId] = useState<string | null>(null);
  const { smartWalletAddress } = useSmartWallet();

  const {
    usdtBalance,
    isLoading: balanceLoading,
    hasFetched: balanceFetched,
  } = useUSDT();

  const {
    merchantDetails,
    merchantConfig,
    sellPrice,
    isLoading: merchantLoading,
    hasFetched: merchantFetched,
  } = useMerchant();
  const [isEditing, setIsEditing] = useState(false);
  const [newUpiId, setNewUpiId] = useState("");

  const handleUpdateUpi = async () => {
    setEncUpiId(newUpiId);
    localStorage.setItem(LocalStorageKeys.MERCHANT_UPI, newUpiId);
    toast({
      title: "Upi Updated Successful",
      description: `Request for Updating upi submitted.`,
    });
    setIsEditing(false);
  };

  useEffect(() => {
    (async () => {
      const upi = await getMerchantUpi();
      if (upi) {
        setEncUpiId(upi);
      }
    })();
  }, []);

  return (
    <div className="w-full rounded-md border border-primary p-4">
      <div className="flex flex-col items-start justify-center gap-2">
        <p className="text-sm text-text-primary">
          Your P2P.me Wallet Address (Base)
        </p>
        <div className="flex items-center gap-2">
          {smartWalletAddress ? (
            <p className="text-sm text-text-light">
              {maskAddress(smartWalletAddress ?? "", 10)}
            </p>
          ) : (
            <Skeleton className="h-6 w-48" />
          )}
          <img
            onClick={() => copyToClipboard(smartWalletAddress as string)}
            className="h-5 w-5 cursor-pointer"
            src={APP_ASSETS.ICONS.PASTE}
            alt={APP_ASSETS.ICONS.PASTE}
          />
        </div>
      </div>
      {!merchantLoading && merchantFetched && encUpiId && (
        <div className="mt-4 flex flex-col items-start justify-center gap-2 text-text-light">
          <div>
            <p className="p-0 text-sm text-text-primary">Your UPI ID</p>
            {isEditing && (
              <div>
                <p className="font-satoshiBold text-start text-[12px] tracking-wide text-text-primary">
                  Current Account No: {merchantConfig?.redactedAccNum}
                </p>
                <p className="font-satoshiBold text-start text-[12px] tracking-wide text-yellow-500">
                  If UPI's Account Number is different, inform the admin to
                  update it.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <input
                  type="text"
                  value={newUpiId}
                  onChange={(e) => setNewUpiId(e.target.value)}
                  className="rounded p-1 text-black"
                />

                <button>
                  <img
                    onClick={handleUpdateUpi}
                    className="cursor-pointer"
                    src={APP_ASSETS.ICONS.CHECK}
                    alt="save"
                  />
                </button>
                <button>
                  <img
                    onClick={() => setIsEditing(false)}
                    className="cursor-pointer"
                    src={APP_ASSETS.ICONS.REJECT}
                    alt="cancel"
                  />
                </button>
              </>
            ) : (
              <>
                <div>{encUpiId}</div>
                <img
                  onClick={() => setIsEditing(true)}
                  className="h-5 w-5 cursor-pointer"
                  src={APP_ASSETS.ICONS.EDIT}
                  alt="Edit"
                />
              </>
            )}
          </div>
        </div>
      )}
      <div className="my-4 border border-primary/30"></div>
      <div className="flex flex-col items-start justify-center gap-2">
        <p className="text-sm text-text-primary">Staked USDC Balance</p>
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex gap-2">
            <img
              className="h-10 w-10"
              src={APP_ASSETS.TOKENS.USDC_BLUE}
              alt="token"
            />
            <div className="flex flex-col">
              <p className="text-sm font-black tracking-wide text-text-light">
                USD Coin
              </p>
              <p className="text-sm font-bold tracking-wide text-gray-400">
                USDC
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className="flex flex-col items-end justify-between gap-1">
              {!merchantLoading &&
              merchantFetched &&
              merchantDetails &&
              sellPrice ? (
                <>
                  <p className="h-6 font-bold text-text-light">
                    {/* //!multiply with p2px sell price */}
                    {((merchantDetails.stake ?? 0) * sellPrice).toFixed(2)}
                  </p>
                  <p className="h-6 font-medium text-text-light">
                    {merchantDetails.stake?.toFixed(2) ?? 0}
                  </p>
                </>
              ) : (
                <>
                  <Skeleton className="h-6 w-8" />
                  <Skeleton className="h-6 w-8" />
                </>
              )}
            </div>
            <div className="flex h-12 flex-col items-center justify-between gap-2">
              <img
                className="h-6"
                src={APP_ASSETS.TOKENS.INR_TRANSPARENT}
                alt="INR"
              />
              <p className="h-6 text-xs font-medium text-gray-400">USDC</p>
            </div>
          </div>
        </div>
      </div>
      <div className="my-4 border border-primary/30"></div>
      <div className="flex flex-col items-center justify-center gap-2">
        <p className="text-sm text-text-primary">Available USDC to stake</p>
        <div className="flex items-baseline gap-2">
          {!balanceLoading && balanceFetched ? (
            <p className="h-6 font-medium tracking-wider text-text-light">
              {usdtBalance?.toFixed(2)}
            </p>
          ) : (
            <Skeleton className="h-6 w-8" />
          )}
          <p className="text-[10px] text-text-light">USDC</p>
        </div>
      </div>
    </div>
  );
};

export default StakeDetails;
